import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface CompatibilityResult {
  score: number;
  grade: string;
  gradeColor: string;
  title: string;
  description: string;
  details: { label: string; score: number; comment: string; algorithm: string }[];
  emoji: string;
  algorithmBreakdown: { stroke: number; ohaeng: number; pronunciation: number; yinyang: number };
}

// ─── 1. 획수 궁합 ─────────────────────────────────────────────────────────
const JAMO_STROKES: Record<string, number> = {
  'ㄱ': 2, 'ㄴ': 2, 'ㄷ': 3, 'ㄹ': 5, 'ㅁ': 4,
  'ㅂ': 4, 'ㅅ': 2, 'ㅇ': 1, 'ㅈ': 3, 'ㅊ': 4,
  'ㅋ': 3, 'ㅌ': 4, 'ㅍ': 4, 'ㅎ': 3, 'ㄲ': 4,
  'ㄸ': 6, 'ㅃ': 8, 'ㅆ': 4, 'ㅉ': 6,
};
const VOWEL_STROKES: Record<string, number> = {
  'ㅏ': 2, 'ㅐ': 3, 'ㅑ': 3, 'ㅒ': 4, 'ㅓ': 2,
  'ㅔ': 3, 'ㅕ': 3, 'ㅖ': 4, 'ㅗ': 2, 'ㅘ': 4,
  'ㅙ': 5, 'ㅚ': 3, 'ㅛ': 3, 'ㅜ': 2, 'ㅝ': 4,
  'ㅞ': 5, 'ㅟ': 3, 'ㅠ': 3, 'ㅡ': 1, 'ㅢ': 2, 'ㅣ': 1,
};
const JONGSEONG = ['', 'ㄱ','ㄲ','ㄳ','ㄴ','ㄵ','ㄶ','ㄷ','ㄹ','ㄺ','ㄻ','ㄼ','ㄽ','ㄾ','ㄿ','ㅀ','ㅁ','ㅂ','ㅄ','ㅅ','ㅆ','ㅇ','ㅈ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const CHOSEONG = ['ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'];
const JUNGSEONG = ['ㅏ','ㅐ','ㅑ','ㅒ','ㅓ','ㅔ','ㅕ','ㅖ','ㅗ','ㅘ','ㅙ','ㅚ','ㅛ','ㅜ','ㅝ','ㅞ','ㅟ','ㅠ','ㅡ','ㅢ','ㅣ'];

function getCharStrokes(char: string): number {
  const code = char.charCodeAt(0);
  if (code < 0xAC00 || code > 0xD7A3) return 1;
  const offset = code - 0xAC00;
  const jong = offset % 28;
  const jung = Math.floor(offset / 28) % 21;
  const cho = Math.floor(offset / 28 / 21);
  const choStroke = JAMO_STROKES[CHOSEONG[cho]] ?? 2;
  const jungStroke = VOWEL_STROKES[JUNGSEONG[jung]] ?? 2;
  const jongStroke = jong > 0 ? (JAMO_STROKES[JONGSEONG[jong]] ?? 2) : 0;
  return choStroke + jungStroke + jongStroke;
}

function getNameStrokes(name: string): number {
  let total = 0;
  for (const ch of name) total += getCharStrokes(ch);
  return total;
}

function getStrokeScore(name1: string, name2: string): number {
  const s1 = getNameStrokes(name1);
  const s2 = getNameStrokes(name2);
  const sum = s1 + s2;
  const digit = sum % 10;
  // 점수 범위 확대: 30~99
  const digitScore: Record<number, number> = {
    1: 88, 2: 55, 3: 93, 4: 30, 5: 82,
    6: 78, 7: 99, 8: 60, 9: 90, 0: 65,
  };
  const diff = Math.abs(s1 - s2);
  const diffBonus = diff <= 2 ? 6 : diff <= 5 ? 2 : diff <= 10 ? -5 : -12;
  return Math.min(99, Math.max(20, (digitScore[digit] ?? 65) + diffBonus));
}

// ─── 2. 오행(五行) 상생/상극 ──────────────────────────────────────────────
const OHAENG_MAP: Record<string, string> = {
  'ㄱ': '목', 'ㄲ': '목', 'ㅋ': '목',
  'ㄴ': '화', 'ㄷ': '화', 'ㄸ': '화', 'ㄹ': '화',
  'ㅇ': '토', 'ㅎ': '토',
  'ㅅ': '금', 'ㅆ': '금', 'ㅈ': '금', 'ㅉ': '금', 'ㅊ': '금',
  'ㅁ': '수', 'ㅂ': '수', 'ㅍ': '수', 'ㅃ': '수',
};

const OHAENG_RELATION: Record<string, Record<string, number>> = {
  목: { 목: 10, 화: 30, 토: -30, 금: -30, 수: 30 },
  화: { 목: 30, 화: 10, 토: 30, 금: -30, 수: -30 },
  토: { 목: -30, 화: 30, 토: 10, 금: 30, 수: -30 },
  금: { 목: -30, 화: -30, 토: 30, 금: 10, 수: 30 },
  수: { 목: 30, 화: -30, 토: -30, 금: 30, 수: 10 },
};

function getChoseong(char: string): string | null {
  const code = char.charCodeAt(0);
  if (code < 0xAC00 || code > 0xD7A3) return null;
  const offset = code - 0xAC00;
  const cho = Math.floor(offset / 28 / 21);
  return CHOSEONG[cho];
}

function getOhaengScore(name1: string, name2: string): number {
  const ohaengs1: string[] = [];
  const ohaengs2: string[] = [];
  for (const ch of name1) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs1.push(OHAENG_MAP[cho]);
  }
  for (const ch of name2) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs2.push(OHAENG_MAP[cho]);
  }
  if (ohaengs1.length === 0 || ohaengs2.length === 0) return 60;

  let totalRelation = 0;
  let count = 0;
  for (const o1 of ohaengs1) {
    for (const o2 of ohaengs2) {
      totalRelation += OHAENG_RELATION[o1]?.[o2] ?? 0;
      count++;
    }
  }
  const avgRelation = count > 0 ? totalRelation / count : 0;

  if (avgRelation > 15) {
    // 상생: 78~99점
    const normalized = (avgRelation - 15) / 15;
    return Math.min(99, Math.max(78, Math.round(78 + normalized * 21)));
  } else if (avgRelation >= -15) {
    // 중립: 52~78점
    const normalized = (avgRelation + 15) / 30;
    return Math.min(78, Math.max(52, Math.round(52 + normalized * 26)));
  } else {
    // 상극: 22~52점 (패널티 강화)
    const normalized = Math.max(0, (avgRelation + 30) / 15);
    return Math.min(52, Math.max(22, Math.round(22 + normalized * 30)));
  }
}

function getOhaengLabel(name1: string, name2: string): string {
  const ohaengs1: string[] = [];
  const ohaengs2: string[] = [];
  for (const ch of name1) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs1.push(OHAENG_MAP[cho]);
  }
  for (const ch of name2) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs2.push(OHAENG_MAP[cho]);
  }
  const e1 = ohaengs1[0] ?? '토';
  const e2 = ohaengs2[0] ?? '토';
  const emojiMap: Record<string, string> = { 목: '🌿', 화: '🔥', 토: '🌍', 금: '⚡', 수: '💧' };
  return `${emojiMap[e1]}${e1} × ${emojiMap[e2]}${e2}`;
}

// ─── 3. 발음 오행 (發音 五行) ─────────────────────────────────────────────
// 이름 각 글자의 초성 오행 흐름이 얼마나 자연스럽게 이어지는지 계산
// 두 이름을 합쳐 발음 오행 시퀀스를 만들고, 연속 상생 흐름 점수를 산출

const OHAENG_CYCLE_SCORE: Record<string, Record<string, number>> = {
  // 상생(相生): 목→화→토→금→수→목
  목: { 목: 5, 화: 20, 토: -15, 금: -15, 수: 15 },
  화: { 목: 15, 화: 5, 토: 20, 금: -15, 수: -15 },
  토: { 목: -15, 화: 15, 토: 5, 금: 20, 수: -15 },
  금: { 목: -15, 화: -15, 토: 15, 금: 5, 수: 20 },
  수: { 목: 20, 화: -15, 토: -15, 금: 15, 수: 5 },
};

function getNameOhaengSequence(name: string): string[] {
  const seq: string[] = [];
  for (const ch of name) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) seq.push(OHAENG_MAP[cho]);
  }
  return seq;
}

function getPronunciationOhaengScore(name1: string, name2: string): number {
  const seq1 = getNameOhaengSequence(name1);
  const seq2 = getNameOhaengSequence(name2);

  if (seq1.length === 0 || seq2.length === 0) return 60;

  // 각 이름의 내부 흐름 점수를 따로 계산 (순서 무관 대칭 보장)
  const calcFlow = (seq: string[]): number => {
    let score = 0;
    for (let i = 0; i < seq.length - 1; i++) {
      score += OHAENG_CYCLE_SCORE[seq[i]]?.[seq[i + 1]] ?? 0;
    }
    return seq.length > 1 ? score / (seq.length - 1) : 0;
  };

  const flow1 = calcFlow(seq1);
  const flow2 = calcFlow(seq2);
  const avgFlow = (flow1 + flow2) / 2;

  // 브릿지 점수: 양방향 평균으로 대칭 보장
  const last1 = seq1[seq1.length - 1];
  const first2 = seq2[0];
  const last2 = seq2[seq2.length - 1];
  const first1 = seq1[0];
  const bridgeScore = (
    (OHAENG_CYCLE_SCORE[last1]?.[first2] ?? 0) +
    (OHAENG_CYCLE_SCORE[last2]?.[first1] ?? 0)
  ) / 2;

  // 점수 범위: 25~99
  const raw = 62 + avgFlow * 1.5 + bridgeScore * 0.5;
  return Math.min(99, Math.max(25, Math.round(raw)));
}

function getPronunciationOhaengLabel(name1: string, name2: string): string {
  const seq1 = getNameOhaengSequence(name1);
  const seq2 = getNameOhaengSequence(name2);
  const emojiMap: Record<string, string> = { 목: '🌿', 화: '🔥', 토: '🌍', 금: '⚡', 수: '💧' };
  const flow1 = seq1.map(o => `${emojiMap[o]}${o}`).join('→');
  const flow2 = seq2.map(o => `${emojiMap[o]}${o}`).join('→');
  return `${flow1} / ${flow2}`;
}

// ─── 4. 음양 균형 (陰陽) ─────────────────────────────────────────────────
const YANG_VOWELS = new Set(['ㅏ', 'ㅗ', 'ㅑ', 'ㅛ', 'ㅐ', 'ㅒ']);
const YIN_VOWELS  = new Set(['ㅓ', 'ㅜ', 'ㅡ', 'ㅕ', 'ㅠ', 'ㅔ', 'ㅖ', 'ㅣ', 'ㅢ', 'ㅝ', 'ㅞ', 'ㅟ', 'ㅘ', 'ㅙ', 'ㅚ']);

function getYinYangRatio(name: string): { yang: number; yin: number } {
  let yang = 0;
  let yin = 0;
  for (const ch of name) {
    const code = ch.charCodeAt(0);
    if (code < 0xAC00 || code > 0xD7A3) continue;
    const offset = code - 0xAC00;
    const jungIdx = Math.floor(offset / 28) % 21;
    const vowel = JUNGSEONG[jungIdx];
    if (YANG_VOWELS.has(vowel)) yang++;
    else if (YIN_VOWELS.has(vowel)) yin++;
  }
  return { yang, yin };
}

function getYinYangScore(name1: string, name2: string): number {
  const r1 = getYinYangRatio(name1);
  const r2 = getYinYangRatio(name2);

  const total1 = r1.yang + r1.yin || 1;
  const total2 = r2.yang + r2.yin || 1;

  // 각 이름의 양 비율 (0~1)
  const yangRatio1 = r1.yang / total1;
  const yangRatio2 = r2.yang / total2;

  // 두 이름의 음양 비율 차이 (보완 관계일수록 높은 점수)
  const diff = Math.abs(yangRatio1 - yangRatio2);

  // 합산 음양 균형 (전체적으로 균형 잡힐수록 보너스)
  const totalYang = r1.yang + r2.yang;
  const totalYin = r1.yin + r2.yin;
  const totalAll = totalYang + totalYin || 1;
  const balanceRatio = Math.min(totalYang, totalYin) / totalAll; // 0~0.5, 0.5에 가까울수록 균형

  // 범위 확대: 보완 관계면 높은 점수, 같은 성향이면 낮은 점수
  const complementScore = diff * 80;   // 0~40점 기여
  const balanceBonus = balanceRatio * 50; // 0~25점 기여
  // 둘 다 같은 음양이면 페널티
  const samePenalty = diff < 0.1 ? -20 : 0;
  const base = 30;

  const raw = base + complementScore + balanceBonus + samePenalty;
  return Math.min(99, Math.max(20, Math.round(raw)));
}

// ─── 해시 함수 ────────────────────────────────────────────────────────────
function hashNames(name1: string, name2: string): number {
  // 사전순 정렬로 대칭 보장
  const [a, b] = [name1, name2].sort();
  const combined = a + '❤️' + b;
  let hash = 0;
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

// ─── 5. 최종 통합 점수 ────────────────────────────────────────────────────
function calculateCompatibility(name1: string, name2: string, relationType: 'love' | 'friend' | 'family'): CompatibilityResult {
  const strokeScore       = getStrokeScore(name1, name2);
  const ohaengScore       = getOhaengScore(name1, name2);
  const pronunciationScore = getPronunciationOhaengScore(name1, name2);
  const yinYangScore      = getYinYangScore(name1, name2);

  // 관계 유형별 고유 시드
  const [sortedA, sortedB] = [name1, name2].sort();
  const seedLove   = hashNames(sortedA, sortedB + ':love');
  const seedFriend = hashNames(sortedA, sortedB + ':friend');
  const seedFamily = hashNames(sortedA, sortedB + ':family');
  const seedMap = { love: seedLove, friend: seedFriend, family: seedFamily };
  const seed = seedMap[relationType];

  // 관계 유형별 가중치
  const weights = {
    love:   { stroke: 0.10, ohaeng: 0.35, yinyang: 0.35, pronunciation: 0.20 },
    friend: { stroke: 0.25, ohaeng: 0.25, yinyang: 0.25, pronunciation: 0.25 },
    family: { stroke: 0.25, ohaeng: 0.25, yinyang: 0.25, pronunciation: 0.25 },
  };

  // 시드 오프셋 확대: ±15점
  const offsets = {
    love:   ((seedLove   % 31) - 15),
    friend: ((seedFriend % 31) - 15),
    family: ((seedFamily % 31) - 15),
  };

  const w = weights[relationType];
  const rawScore = strokeScore * w.stroke + ohaengScore * w.ohaeng + pronunciationScore * w.pronunciation + yinYangScore * w.yinyang;

  // 오행 상극 패널티 강화 (0.18 → 0.15)
  // 다른 요소(수리, 음양)가 좋으면 충분히 높은 점수 가능
  const ohaengPenalty = ohaengScore < 52 ? (52 - ohaengScore) * 0.15 : 0;
  const adjustedRaw = rawScore - ohaengPenalty;

  // 전체 점수 범위: 20~99 (하한선 낮춤)
  const baseScore = Math.min(99, Math.max(20, Math.round(adjustedRaw + offsets[relationType])));

  // 세부 항목 점수
  const shiftMap = {
    love:   [3, 7, 11, 15],
    friend: [5, 9, 13, 17],
    family: [2, 6, 10, 14],
  };
  const shifts = shiftMap[relationType];

  const detailBase = [
    Math.round((strokeScore + yinYangScore) / 2),
    Math.round((ohaengScore + yinYangScore) / 2),
    Math.round((strokeScore + ohaengScore) / 2),
    Math.round((pronunciationScore + ohaengScore) / 2),
  ];

  const detailSeeds = detailBase.map((base, i) => {
    const offset = ((seed >> shifts[i]) % 37) - 18;
    return Math.min(99, Math.max(35, base + offset));
  });

  const bonusMap = {
    love:   [0, 8, 5, 0],
    friend: [7, 0, 5, 0],
    family: [0, 6, 0, 7],
  };
  const bonuses = bonusMap[relationType];
  const adjustedDetails = detailSeeds.map((s, i) => Math.min(99, s + bonuses[i]));

  const ohaengLbl = getOhaengLabel(name1, name2);
  const pronLbl   = getPronunciationOhaengLabel(name1, name2);
  const s1 = getNameStrokes(name1);
  const s2 = getNameStrokes(name2);

  const detailLabels = {
    love:   ['💬 대화 궁합', '💞 감정 궁합', '🎯 취향 궁합', '🌟 미래 궁합'],
    friend: ['💬 대화 궁합', '🤝 신뢰 궁합', '🎮 취미 궁합', '🌈 에너지 궁합'],
    family: ['💬 대화 궁합', '🤗 유대 궁합', '🏠 생활 궁합', '💪 지지 궁합'],
  };

  const detailAlgorithms = [
    `음양 균형 (${name1} × ${name2})`,
    `오행 상생/상극 (${ohaengLbl})`,
    `획수 궁합 (${s1}획 + ${s2}획)`,
    `발음 오행 흐름 (${pronLbl})`,
  ];

  const detailComments: Record<string, string[][]> = {
    love: [
      ['말이 잘 통하고 대화가 끊이지 않아요!', '서로 이해하려는 노력이 빛을 발해요.', '처음엔 어색해도 시간이 지나면 편해져요.'],
      ['감정 표현이 잘 맞아 서로 따뜻하게 느껴요!', '감정 기복을 서로 잘 받아줄 수 있어요.', '감정 표현 방식이 달라 노력이 필요해요.'],
      ['취미와 관심사가 놀랍도록 잘 맞아요!', '다른 취향이 오히려 서로를 품요롭게 해요.', '취향 차이가 있지만 그게 매력이 될 수 있어요.'],
      ['함께하는 미래가 밝고 기대돼요!', '서로 맞춰가며 좋은 미래를 만들 수 있어요.', '방향이 달라도 함께라면 길을 찾을 수 있어요.'],
    ],
    friend: [
      ['말이 잘 통하고 웃음이 끊이지 않아요!', '서로 솔직하게 이야기할 수 있는 사이예요.', '처음엔 어색해도 금방 친해질 수 있어요.'],
      ['믿고 의지할 수 있는 든든한 친구예요!', '서로의 비밀을 잘 지켜줄 수 있어요.', '신뢰를 쌓아가면 더 가까워질 수 있어요.'],
      ['같이 놀면 시간 가는 줄 모르는 조합!', '취미가 달라도 함께하면 재미있어요.', '새로운 취미를 함께 찾아보면 좋겠어요.'],
      ['함께 있으면 에너지가 넘쳐요!', '서로의 에너지를 잘 보완해줘요.', '에너지 차이가 있지만 균형을 맞출 수 있어요.'],
    ],
    family: [
      ['가족끼리 대화가 잘 통하는 편이에요!', '서로를 이해하려는 마음이 있어요.', '대화 방식이 달라도 사랑으로 극복할 수 있어요.'],
      ['끈끈한 가족 유대감이 느껴져요!', '서로를 아끼는 마음이 잘 전달돼요.', '유대감을 더 쌓아가면 더 가까워질 수 있어요.'],
      ['생활 패턴이 잘 맞아 함께 지내기 편해요!', '서로 배려하며 잘 맞춰갈 수 있어요.', '생활 방식이 달라도 존중하면 괜찮아요.'],
      ['힘들 때 서로 든든한 버팀목이 돼요!', '서로를 응원하고 지지해줄 수 있어요.', '지지하는 방식이 달라도 마음은 같아요.'],
    ],
  };

  const labels = detailLabels[relationType];
  const comments = detailComments[relationType];

  const details = adjustedDetails.map((s, i) => ({
    label: labels[i],
    score: s,
    comment: s >= 80 ? comments[i][0] : s >= 65 ? comments[i][1] : comments[i][2],
    algorithm: detailAlgorithms[i],
  }));

  const relationTitles: Record<string, { s: string; a: string; b: string; c: string; d: string }> = {
    love:   { s: '운명적인 소울메이트 ✨', a: '찰떡 케미 커플 💕', b: '좋은 인연 🌼', c: '노력형 커플 🌱', d: '도전적인 인연 🔥' },
    friend: { s: '천생 베프 조합 ✨',     a: '찰떡 친구 케미 🤝', b: '좋은 친구 사이 🌼', c: '노력형 우정 🌱', d: '도전적인 우정 🔥' },
    family: { s: '완벽한 가족 케미 ✨',   a: '찰떡 가족 궁합 🏠', b: '좋은 가족 인연 🌼', c: '노력형 가족 🌱', d: '도전적인 가족 관계 🔥' },
  };

  const relationDesc: Record<string, { s: string; a: string; b: string; c: string; d: string }> = {
    love: {
      s: `${name1}님과 ${name2}님은 전생에도 함께였을 것 같아요. 오행 상생에 수리 사주까지 완벽하게 맞아떨어지는 조합! 주변 사람들도 두 분의 케미에 감탄할 거예요. 이런 인연은 정말 드물답니다 💫`,
      a: `${name1}님과 ${name2}님, 이름부터 잘 어울리는 조합이에요! 서로의 장점을 끌어올려주는 시너지가 대단해요. 함께할수록 더 빛나는 두 분의 관계, 정말 부럽네요 🌸`,
      b: `${name1}님과 ${name2}님은 서로에게 좋은 영향을 주는 사이예요. 완벽하진 않아도 함께 노력하면 더 좋아질 수 있는 가능성이 충분해요 🌻`,
      c: `${name1}님과 ${name2}님은 처음엔 어색할 수 있지만, 알아갈수록 서로의 매력을 발견하게 되는 조합이에요. 다름을 인정하고 맞춰가는 과정에서 더 깊은 인연이 될 수 있어요 🌿`,
      d: `${name1}님과 ${name2}님은 서로 다른 에너지를 가진 조합이에요. 오행 상극 관계지만 반대 에너지가 오히려 강한 끌림이 되기도 하죠! 어려운 만큼 이뤄냈을 때 더 값진 인연이 될 거예요 💪`,
    },
    friend: {
      s: `${name1}님과 ${name2}님은 만나자마자 통하는 천생 베프 조합이에요! 수리 사주와 음양 균형까지 완벽한 조합. 함께 있으면 시간 가는 줄 모르고, 서로에게 최고의 에너지를 주는 사이예요 💫`,
      a: `${name1}님과 ${name2}님, 이름부터 잘 어울리는 친구 조합이에요! 서로의 장점을 끌어올려주는 시너지가 대단해요. 함께할수록 더 빛나는 우정이에요 🌸`,
      b: `${name1}님과 ${name2}님은 서로에게 좋은 영향을 주는 친구 사이예요. 함께 노력하면 더 깊은 우정으로 발전할 수 있어요 🌻`,
      c: `${name1}님과 ${name2}님은 처음엔 어색할 수 있지만, 알아갈수록 서로의 매력을 발견하게 되는 조합이에요. 노력하면 오래가는 우정이 될 수 있어요 🌿`,
      d: `${name1}님과 ${name2}님은 서로 다른 에너지를 가진 조합이에요. 다름이 오히려 서로를 성장시키는 자극이 될 수 있어요! 도전적인 만큼 값진 우정이에요 💪`,
    },
    family: {
      s: `${name1}님과 ${name2}님은 이름에서부터 완벽한 가족 에너지가 느껴져요! 수리 사주 4격이 모두 길수인 최고의 조합. 서로를 깊이 이해하고 아끼는 끈끈한 가족 관계예요 💫`,
      a: `${name1}님과 ${name2}님, 이름부터 잘 어울리는 가족 조합이에요! 서로의 장점을 끌어올려주는 시너지가 대단해요. 함께할수록 더 빛나는 가족 관계예요 🌸`,
      b: `${name1}님과 ${name2}님은 서로에게 좋은 영향을 주는 가족 사이예요. 함께 노력하면 더 깊은 유대감으로 발전할 수 있어요 🌻`,
      c: `${name1}님과 ${name2}님은 생활 방식이 달라 노력이 필요하지만, 서로를 이해하려는 마음이 있다면 더 가까워질 수 있어요 🌿`,
      d: `${name1}님과 ${name2}님은 서로 다른 에너지를 가진 가족 조합이에요. 다름을 인정하고 존중하면 더 단단한 가족 관계가 될 수 있어요 💪`,
    },
  };

  let grade: string;
  let gradeColor: string;
  let title: string;
  let description: string;
  let emoji: string;

  const t = relationTitles[relationType];
  const d = relationDesc[relationType];

  if (baseScore >= 90) {
    grade = 'S'; gradeColor = 'from-yellow-400 to-orange-400'; title = t.s; description = d.s;
    emoji = relationType === 'love' ? '💍' : relationType === 'friend' ? '🏆' : '🏡';
  } else if (baseScore >= 78) {
    grade = 'A'; gradeColor = 'from-pink-400 to-rose-400'; title = t.a; description = d.a;
    emoji = relationType === 'love' ? '💕' : relationType === 'friend' ? '🤝' : '💛';
  } else if (baseScore >= 65) {
    grade = 'B'; gradeColor = 'from-orange-400 to-amber-400'; title = t.b; description = d.b; emoji = '🌼';
  } else if (baseScore >= 52) {
    grade = 'C'; gradeColor = 'from-teal-400 to-cyan-400'; title = t.c; description = d.c; emoji = '🌱';
  } else {
    grade = 'D'; gradeColor = 'from-slate-400 to-gray-400'; title = t.d; description = d.d; emoji = '🔥';
  }

  return {
    score: baseScore,
    grade,
    gradeColor,
    title,
    description,
    details,
    emoji,
    algorithmBreakdown: {
      stroke: Math.round(strokeScore),
      ohaeng: Math.round(ohaengScore),
      pronunciation: Math.round(pronunciationScore),
      yinyang: Math.round(yinYangScore),
    },
  };
}

// ─── 스토리 설명 함수들 ────────────────────────────────────────────────────
function getStrokeStory(score: number): string {
  if (score >= 85) return '두 이름의 획수 리듬이 비슷해 일상에서 마찰이 적은 조합입니다.';
  if (score >= 70) return '두 이름의 획수 흐름이 서로를 자연스럽게 이어주는 편입니다.';
  return '획수 차이가 있지만 그 차이가 오히려 강한 인상을 남기기도 해요.';
}

function getOhaengStory(name1: string, name2: string, score: number): string {
  const ohaengs1: string[] = [];
  const ohaengs2: string[] = [];
  for (const ch of name1) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs1.push(OHAENG_MAP[cho]);
  }
  for (const ch of name2) {
    const cho = getChoseong(ch);
    if (cho && OHAENG_MAP[cho]) ohaengs2.push(OHAENG_MAP[cho]);
  }
  const e1 = ohaengs1[0] ?? '토';
  const e2 = ohaengs2[0] ?? '토';
  if (e1 === e2) return `두 이름 모두 ${e1}의 기운을 지녀 같은 방향을 바라보는 조합입니다.`;
  if (score >= 80) return '서로 다른 기질이 균형을 만들어주는 상생 관계입니다.';
  if (score >= 65) return `${e1}과 ${e2}의 기운이 서로를 자극하며 성장시키는 관계입니다.`;
  return `${e1}과 ${e2}의 기운이 달라 서로의 차이를 이해하는 노력이 빛을 발하는 조합입니다.`;
}

function getYinYangStory(name1: string, name2: string, score: number): string {
  const r1 = getYinYangRatio(name1);
  const r2 = getYinYangRatio(name2);
  const total1 = r1.yang + r1.yin || 1;
  const total2 = r2.yang + r2.yin || 1;
  const yangRatio1 = Math.round((r1.yang / total1) * 100);
  const yangRatio2 = Math.round((r2.yang / total2) * 100);
  if (score >= 80) return `${name1}(양 ${yangRatio1}%)와 ${name2}(양 ${yangRatio2}%)의 음양이 서로를 완벽하게 보완하는 조합입니다.`;
  if (score >= 65) return `${name1}(양 ${yangRatio1}%)와 ${name2}(양 ${yangRatio2}%)의 음양 균형이 잘 맞는 편이에요.`;
  return `${name1}(양 ${yangRatio1}%)와 ${name2}(양 ${yangRatio2}%)의 음양 성향이 비슷해 서로 자극이 필요한 조합이에요.`;
}

function getPronunciationOhaengStory(name1: string, name2: string, score: number): string {
  const seq1 = getNameOhaengSequence(name1);
  const seq2 = getNameOhaengSequence(name2);
  const emojiMap: Record<string, string> = { 목: '🌿', 화: '🔥', 토: '🌍', 금: '⚡', 수: '💧' };
  const last1 = seq1[seq1.length - 1] ?? '토';
  const first2 = seq2[0] ?? '토';
  const last2 = seq2[seq2.length - 1] ?? '토';
  const first1 = seq1[0] ?? '토';
  const bridgeRelation =
    ((OHAENG_CYCLE_SCORE[last1]?.[first2] ?? 0) +
     (OHAENG_CYCLE_SCORE[last2]?.[first1] ?? 0)) / 2;

  if (score >= 85) {
    return `두 이름의 발음 오행이 ${emojiMap[last1]}${last1}↔${emojiMap[first2]}${first2} 상생 흐름으로 이어져 말할 때 자연스럽고 편안한 느낌을 줘요.`;
  }
  if (score >= 70) {
    return `발음 오행 흐름이 전반적으로 부드럽게 연결돼요. 두 이름을 함께 부를 때 리듬감이 느껴져요.`;
  }
  if (bridgeRelation < 0) {
    return `${emojiMap[last1]}${last1}↔${emojiMap[first2]}${first2} 연결에서 약간의 긴장감이 있지만, 그 차이가 오히려 강한 인상을 남기기도 해요.`;
  }
  return `발음 오행 흐름에 변화가 많아 개성 있는 조합이에요. 서로 다른 에너지가 만나는 느낌이에요.`;
}

// ─── ScoreBar 컴포넌트 ────────────────────────────────────────────────────
function ScoreBar({ score, delay }: { score: number; delay: number }) {
  return (
    <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-pink-400 to-rose-500 transition-all duration-700 ease-out"
        style={{ width: `${score}%`, transitionDelay: `${delay}ms` }}
      />
    </div>
  );
}

// ─── AlgorithmBreakdown 컴포넌트 ──────────────────────────────────────────
function AlgorithmBreakdown({ breakdown, name1, name2, show }: {
  breakdown: { stroke: number; ohaeng: number; pronunciation: number; yinyang: number };
  name1: string;
  name2: string;
  show: boolean;
}) {
  const strokeStory      = getStrokeStory(breakdown.stroke);
  const ohaengStory      = getOhaengStory(name1, name2, breakdown.ohaeng);
  const pronunciationStory = getPronunciationOhaengStory(name1, name2, breakdown.pronunciation);
  const yinYangStory     = getYinYangStory(name1, name2, breakdown.yinyang);

  const items = [
    {
      icon: '✍️',
      label: `획수 궁합 · ${breakdown.stroke}점`,
      story: strokeStory,
      color: 'bg-amber-50 border-amber-100',
      iconBg: 'bg-amber-100 text-amber-600',
      labelColor: 'text-amber-600',
    },
    {
      icon: '☯️',
      label: `오행 상생/상극 · ${breakdown.ohaeng}점`,
      story: ohaengStory,
      color: 'bg-teal-50 border-teal-100',
      iconBg: 'bg-teal-100 text-teal-600',
      labelColor: 'text-teal-600',
    },
    {
      icon: '🎵',
      label: `발음 오행 · ${breakdown.pronunciation}점`,
      story: pronunciationStory,
      color: 'bg-rose-50 border-rose-100',
      iconBg: 'bg-rose-100 text-rose-500',
      labelColor: 'text-rose-600',
    },
    {
      icon: '🌙',
      label: `음양 균형 · ${breakdown.yinyang}점`,
      story: yinYangStory,
      color: 'bg-indigo-50 border-indigo-100',
      iconBg: 'bg-indigo-100 text-indigo-500',
      labelColor: 'text-indigo-600',
    },
  ];

  return (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      <div className="px-6 py-4 border-b border-gray-100">
        <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
          <span>🔬</span> 알고리즘 분석
        </h3>
        <p className="text-xs text-gray-400 mt-0.5">4가지 방식으로 분석한 궁합 이야기예요</p>
      </div>
      <div className="px-6 py-6 space-y-5">
        {items.map((item, i) => (
          <div
            key={i}
            className={`flex items-start gap-3 rounded-xl border p-4 ${item.color} transition-all duration-500`}
            style={{ transitionDelay: `${i * 150 + 200}ms`, opacity: show ? 1 : 0, transform: show ? 'translateY(0)' : 'translateY(12px)' }}
          >
            <div className={`w-9 h-9 flex items-center justify-center rounded-xl text-lg flex-shrink-0 ${item.iconBg}`}>
              {item.icon}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-xs font-bold mb-1 ${item.labelColor}`}>{item.label}</p>
              <p className="text-sm text-gray-600 leading-relaxed">{item.story}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── 메인 페이지 컴포넌트 ─────────────────────────────────────────────────
export default function Compatibility() {
  const [name1, setName1] = useState('');
  const [name2, setName2] = useState('');
  const [relationType, setRelationType] = useState<'love' | 'friend' | 'family'>('love');
  const [result, setResult] = useState<CompatibilityResult | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [error1, setError1] = useState('');
  const [error2, setError2] = useState('');
  const [showScore, setShowScore] = useState(false);
  const [copied, setCopied] = useState(false);
  const [instaToast, setInstaToast] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const validateName = (name: string): string => {
    if (!name.trim()) return '이름을 입력해주세요.';
    if (!/^[가-힣]+$/.test(name.trim())) return '한글 이름만 입력해주세요.';
    if (name.trim().length < 2) return '2글자 이상 입력해주세요.';
    if (name.trim().length > 5) return '5글자 이하로 입력해주세요.';
    return '';
  };

  const calculate = () => {
    const e1 = validateName(name1);
    const e2 = validateName(name2);
    setError1(e1);
    setError2(e2);
    if (e1 || e2) return;

    setIsAnimating(true);
    setShowScore(false);
    setResult(null);

    setTimeout(() => {
      const res = calculateCompatibility(name1.trim(), name2.trim(), relationType);
      setResult(res);
      setIsAnimating(false);
      setTimeout(() => {
        setShowScore(true);
        resultRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }, 1800);
  };

  const shareText = result
    ? `💕 이름 궁합 테스트 결과\n\n${name1} ❤️ ${name2}\n\n궁합 점수: ${result.score}점 (${result.grade}등급)\n${result.title}\n\n나도 해보기 → ${window.location.href}`
    : '';

  const copyResult = () => {
    navigator.clipboard.writeText(shareText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareToThreads = () => {
    window.open(`https://www.threads.net/intent/post?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  };

  const shareToKakao = () => {
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      navigator.share({
        title: `💕 이름 궁합 테스트 결과`,
        text: `${name1} ❤️ ${name2}\n궁합 점수: ${result?.score}점 (${result?.grade}등급)\n${result?.title}`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(shareText).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      });
    }
  };

  const shareToInstagram = () => {
    navigator.clipboard.writeText(shareText).then(() => {
      setInstaToast(true);
      setTimeout(() => setInstaToast(false), 3000);
    });
  };

  const goToHome = () => {
    navigate('/');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }, 0);
  };

  const relationTypeConfig = {
    love: { icon: '💕', label: '연인', activeClass: 'bg-pink-500 text-white shadow-md', inactiveClass: 'bg-gray-100 text-gray-500 hover:bg-pink-50 hover:text-pink-500' },
    friend: { icon: '🤝', label: '우정', activeClass: 'bg-amber-400 text-white shadow-md', inactiveClass: 'bg-gray-100 text-gray-500 hover:bg-amber-50 hover:text-amber-500' },
    family: { icon: '🏠', label: '가족', activeClass: 'bg-teal-500 text-white shadow-md', inactiveClass: 'bg-gray-100 text-gray-500 hover:bg-teal-50 hover:text-teal-500' },
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-compatibility';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/compatibility#webpage`,
      url: `${import.meta.env.VITE_SITE_URL ?? ''}/compatibility`,
      name: '이름 궁합률 테스트 - 두 이름의 궁합 점수 확인',
      description: '두 이름을 입력하면 획수·오행·음양·발음 오행 4가지 방식으로 궁합 점수를 분석해드려요. 연인, 친구, 가족 궁합 모두 확인 가능!',
      inLanguage: 'ko',
      isPartOf: {
        '@id': `${import.meta.env.VITE_SITE_URL ?? ''}/#website`,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '홈', item: import.meta.env.VITE_SITE_URL ?? '' },
          { '@type': 'ListItem', position: 2, name: '이름 궁합률 테스트', item: `${import.meta.env.VITE_SITE_URL ?? ''}/compatibility` },
        ],
      },
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('schema-compatibility')?.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50">
      {/* Tab Navigation */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex">
            <button
              onClick={() => navigate('/')}
              className="flex-1 py-4 text-sm font-semibold text-center transition-all cursor-pointer border-b-2 border-transparent text-gray-400 hover:text-rose-500 whitespace-nowrap"
            >
              🌍 글로벌 이름
            </button>
            <button
              onClick={() => navigate('/compatibility')}
              className="flex-1 py-4 text-sm font-semibold text-center transition-all cursor-pointer border-b-2 border-pink-500 text-pink-600 whitespace-nowrap"
            >
              💕 이름 궁합
            </button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-10 pb-6 px-4 md:pt-20 md:pb-12">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-pink-500 via-rose-500 to-orange-400 bg-clip-text text-transparent">
            이름 궁합률 테스트
          </h1>
          <p className="text-lg md:text-xl text-gray-500">
            두 이름의 에너지가 얼마나 잘 맞는지 알아보세요 💕
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-gray-400">
            <span className="flex items-center gap-1 bg-white/80 border border-gray-100 rounded-full px-3 py-1 shadow-sm">✍️ 획수 궁합</span>
            <span className="flex items-center gap-1 bg-white/80 border border-gray-100 rounded-full px-3 py-1 shadow-sm">☯️ 오행 상생/상극</span>
            <span className="flex items-center gap-1 bg-white/80 border border-gray-100 rounded-full px-3 py-1 shadow-sm">🌙 음양 균형</span>
            <span className="flex items-center gap-1 bg-white/80 border border-gray-100 rounded-full px-3 py-1 shadow-sm">🎵 발음 오행</span>
          </div>
        </div>
      </section>

      {/* Input Section */}
      <section className="py-4 px-4 md:py-8">
        <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl p-6 md:p-10">
          <div className="flex flex-col gap-6">
            {/* Name 1 */}
            <div>
              <label className="block text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">첫 번째 이름</label>
              <input
                type="text"
                value={name1}
                onChange={(e) => { setName1(e.target.value); setError1(''); }}
                placeholder="예) 김민수"
                className={`w-full py-4 px-5 text-lg border-2 rounded-xl focus:outline-none transition-colors ${error1 ? 'border-red-400' : 'border-gray-200 focus:border-pink-400'}`}
                onKeyDown={(e) => e.key === 'Enter' && calculate()}
              />
              {error1 && <p className="mt-2 text-sm text-red-500 flex items-center gap-1"><i className="ri-error-warning-line"></i>{error1}</p>}
            </div>

            {/* Relation Type Selector */}
            <div className="flex items-center w-full gap-3">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent to-pink-200" />
              <div className="flex items-center gap-2 bg-gray-50 rounded-2xl p-1.5 border border-gray-100">
                {(Object.keys(relationTypeConfig) as Array<'love' | 'friend' | 'family'>).map((type) => {
                  const cfg = relationTypeConfig[type];
                  return (
                    <button
                      key={type}
                      onClick={() => setRelationType(type)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${relationType === type ? cfg.activeClass : cfg.inactiveClass}`}
                    >
                      <span>{cfg.icon}</span>
                      <span>{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex-1 h-px bg-gradient-to-l from-transparent to-pink-200" />
            </div>

            {/* Name 2 */}
            <div>
              <label className="block text-sm font-semibold text-gray-500 mb-2 uppercase tracking-wide">두 번째 이름</label>
              <input
                type="text"
                value={name2}
                onChange={(e) => { setName2(e.target.value); setError2(''); }}
                placeholder="예) 이지은"
                className={`w-full py-4 px-5 text-lg border-2 rounded-xl focus:outline-none transition-colors ${error2 ? 'border-red-400' : 'border-gray-200 focus:border-pink-400'}`}
                onKeyDown={(e) => e.key === 'Enter' && calculate()}
              />
              {error2 && <p className="mt-2 text-sm text-red-500 flex items-center gap-1"><i className="ri-error-warning-line"></i>{error2}</p>}
            </div>

            {/* Button */}
            <button
              onClick={calculate}
              disabled={isAnimating}
              className={`relative w-full py-5 text-white text-xl font-bold rounded-xl shadow-lg transition-all whitespace-nowrap cursor-pointer bg-gradient-to-r from-pink-500 to-rose-500 hover:shadow-xl hover:scale-105 overflow-hidden ${isAnimating ? 'opacity-60 cursor-not-allowed hover:scale-100' : ''}`}
            >
              <span className="relative z-10">
                {isAnimating ? '궁합 분석 중... 💫' : '궁합 확인하기 💕'}
              </span>
              {!isAnimating && (
                <span className="absolute inset-0 z-0 shimmer-light" />
              )}
            </button>

            {/* 위치2: 버튼 아래 광고 배너 */}
            <a
              href="https://bookk.co.kr/bookStore/68d3f712ac751d87d0a8489c"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full cursor-pointer"
            >
              <img
                src="https://storage.readdy-site.link/project_files/a54e172b-202b-43f9-932c-f9b2f2c2da78/8897fae7-6e71-4515-b662-5f6edbfb0f06_Untitled-1.jpg?v=667f8f7ac8e9a6eb98144c1d58b57c25"
                alt="광고 배너"
                className="w-full rounded-xl object-cover"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Loading Animation */}
      {isAnimating && (
        <section className="py-12 px-4">
          <div className="max-w-md mx-auto text-center">
            <div className="text-6xl mb-6 animate-bounce">
              {relationType === 'love' ? '💘' : relationType === 'friend' ? '🤝' : '🏠'}
            </div>
            <p className="text-lg text-gray-600 font-medium mb-2">두 이름의 에너지를 분석하는 중...</p>
            <p className="text-xs text-gray-400 mb-4">획수 · 오행 · 음양 균형 · 발음 오행 4가지 방식으로 계산 중이에요</p>
            <div className="flex justify-center gap-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-3 h-3 rounded-full bg-pink-400 animate-bounce"
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Result Section */}
      {result && !isAnimating && (
        <section ref={resultRef} className="py-8 px-4 md:py-12">
          <div className="max-w-2xl mx-auto space-y-6">

            {/* Score Card */}
            <div className={`bg-white rounded-3xl shadow-xl overflow-hidden transition-all duration-700 ${showScore ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <div className={`bg-gradient-to-r ${result.gradeColor} p-8 text-center text-white`}>
                <div className="text-5xl mb-3">{result.emoji}</div>
                <div className="text-7xl font-black mb-2">{result.score}<span className="text-3xl font-bold">점</span></div>
                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 text-lg font-bold mb-3">
                  {result.grade}등급
                </div>
                <h2 className="text-2xl font-bold">{result.title}</h2>
              </div>

              {/* Names Display */}
              <div className="px-8 py-6 border-b border-gray-100">
                <div className="flex items-center justify-center gap-4">
                  <div className="text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-pink-100 text-2xl font-bold text-pink-600 mx-auto mb-1">
                      {name1.trim()[0]}
                    </div>
                    <p className="text-sm font-semibold text-gray-700">{name1.trim()}</p>
                  </div>
                  <div className="text-3xl">
                    {relationType === 'love' ? '❤️' : relationType === 'friend' ? '🤝' : '🏠'}
                  </div>
                  <div className="text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-rose-100 text-2xl font-bold text-rose-600 mx-auto mb-1">
                      {name2.trim()[0]}
                    </div>
                    <p className="text-sm font-semibold text-gray-700">{name2.trim()}</p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="px-6 py-6 border-b border-gray-100 md:px-8">
                <p className="text-gray-600 leading-relaxed text-center text-sm md:text-base break-keep word-break-keep-all">{result.description}</p>
              </div>

              {/* Detail Scores */}
              <div className="px-6 py-6 space-y-5 md:px-8">
                {result.details.map((detail, i) => (
                  <div key={i}>
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-semibold text-gray-700">{detail.label}</span>
                      <span className="text-sm font-bold text-pink-500">{detail.score}점</span>
                    </div>
                    <ScoreBar score={showScore ? detail.score : 0} delay={i * 150} />
                    <div className="flex items-start justify-between mt-1 gap-2">
                      <p className="text-xs text-gray-400 break-keep leading-relaxed">{detail.comment}</p>
                      <p className="text-xs text-gray-300 whitespace-nowrap hidden sm:block">{detail.algorithm}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Algorithm Breakdown */}
            {showScore && (
              <AlgorithmBreakdown
                breakdown={result.algorithmBreakdown}
                name1={name1.trim()}
                name2={name2.trim()}
                show={showScore}
              />
            )}

            {/* Share Buttons */}
            {showScore && (
              <>
                {/* 위치1: 공유 버튼 위 광고 배너 */}
                <a
                  href="https://bookk.co.kr/bookStore/68d3f712ac751d87d0a8489c"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full cursor-pointer"
                >
                  <img
                    src="https://storage.readdy-site.link/project_files/a54e172b-202b-43f9-932c-f9b2f2c2da78/8897fae7-6e71-4515-b662-5f6edbfb0f06_Untitled-1.jpg?v=667f8f7ac8e9a6eb98144c1d58b57c25"
                    alt="광고 배너"
                    className="w-full rounded-2xl object-cover"
                  />
                </a>

                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <p className="text-center text-sm font-semibold text-gray-600 mb-4">결과 공유하기</p>
                  <div className="flex justify-center gap-5">
                    {/* 카카오톡 */}
                    <button onClick={shareToKakao} className="flex flex-col items-center gap-2 cursor-pointer group">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#FEE500] hover:bg-[#f0d800] transition-all group-hover:scale-110 shadow-md">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 3C6.477 3 2 6.477 2 10.8c0 2.7 1.6 5.08 4.03 6.52L5 21l4.5-2.4c.82.18 1.67.28 2.5.28 5.523 0 10-3.477 10-7.8S17.523 3 12 3z" fill="#3A1D1D"/>
                        </svg>
                      </div>
                      <span className="text-xs text-gray-500">카카오톡</span>
                    </button>

                    {/* 인스타그램 */}
                    <button onClick={shareToInstagram} className="flex flex-col items-center gap-2 cursor-pointer group relative">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gradient-to-br from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] hover:opacity-90 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-instagram-line text-xl text-white"></i>
                      </div>
                      <span className="text-xs text-gray-500">인스타</span>
                      {instaToast && (
                        <div className="absolute -top-14 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs py-2 px-3 rounded-lg whitespace-nowrap text-center leading-relaxed">
                          텍스트 복사 완료!<br/>인스타에 붙여넣기 하세요 📋
                        </div>
                      )}
                    </button>

                    {/* 스레드 */}
                    <button onClick={shareToThreads} className="flex flex-col items-center gap-2 cursor-pointer group">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-900 hover:bg-gray-700 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-threads-line text-lg text-white"></i>
                      </div>
                      <span className="text-xs text-gray-500">스레드</span>
                    </button>

                    {/* 링크복사 */}
                    <button onClick={copyResult} className="flex flex-col items-center gap-2 cursor-pointer group relative">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-all group-hover:scale-110 shadow-md">
                        <i className="ri-clipboard-line text-lg text-gray-700"></i>
                      </div>
                      <span className="text-xs text-gray-500">링크복사</span>
                      {copied && (
                        <div className="absolute -top-9 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs py-1.5 px-3 rounded-lg whitespace-nowrap">
                          복사 완료!
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Try Again */}
            <div className="text-center">
              <button
                onClick={() => { setResult(null); setName1(''); setName2(''); setShowScore(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="text-sm text-gray-400 hover:text-pink-500 transition-colors cursor-pointer underline underline-offset-2"
              >
                다시 테스트하기
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 mt-8 bg-pink-50 border-t border-pink-100">
        <div className="max-w-2xl mx-auto flex flex-col items-center gap-6">

          {/* 글로벌 이름 짓기 배너 */}
          <div onClick={goToHome} className="w-full block cursor-pointer group">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-violet-500 via-rose-500 to-indigo-500 p-5 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              <div className="relative flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-bold bg-white/25 text-white px-2 py-0.5 rounded-full tracking-wide">NEW ✨</span>
                  </div>
                  <h3 className="text-white font-bold text-lg leading-tight mb-1">
                    🌍 글로벌 이름 짓기
                  </h3>
                  <p className="text-white/80 text-sm">
                    세계 10개국 스타일로 나만의 이름을 만들어보세요 🌟
                  </p>
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 group-hover:bg-white/30 transition-all flex-shrink-0">
                  <i className="ri-arrow-right-line text-white text-lg"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="w-full bg-white/70 border border-pink-100 rounded-2xl px-5 py-4 text-center">
            <p className="text-lg text-gray-900 leading-relaxed">
              재미로 보는 이름 궁합 테스트입니다. 과학적 근거가 없으니 가볍게 즐겨주세요 😊
            </p>
          </div>

          <div className="flex items-center gap-6 text-sm text-gray-500">
            <Link to="/about" className="hover:text-purple-600 transition-colors cursor-pointer">About</Link>
            <span className="text-gray-300">|</span>
            <Link to="/contact" className="hover:text-purple-600 transition-colors cursor-pointer">Contact</Link>
            <span className="text-gray-300">|</span>
            <Link to="/privacy" className="hover:text-purple-600 transition-colors cursor-pointer">Privacy Policy</Link>
          </div>
          <p className="text-xs text-gray-400">© 2026 나홀로 사장. All rights reserved.</p>
        </div>
      </footer>
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(250%) skewX(-15deg); }
        }
        .shimmer-light {
          display: block;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent);
          animation: shimmer 2.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
